import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Rocket, Sparkles, Wand2, Bot, Scale, Trash2, Plus, Upload, Save, Image as ImageIcon, ArrowLeft, Pencil, Calendar } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp } from "../../lib/format";
import { recommendPricing, recommendFairnessCap, divideInstantWins, randomSkill } from "../../lib/aiTools";
import { findLaunchComps, getLaunchAt, LAUNCH_TAG, LAUNCH_AT_PREFIX } from "../../lib/launch";

const CATEGORIES = ["Tech", "Holidays", "Cars", "Cash", "Watches", "Home"];

const EMPTY = {
  launch_date: "2026-08-01", launch_time: "21:00",
  title: "£50,000 Cash Launch Special", prize_title: "£50,000 in a Metal Briefcase",
  description: "A headline launch-night draw — win a sleek metal briefcase packed with £50,000 in cash. Drawn LIVE at 9pm on launch night.",
  hero_image: "", category: "Cash",
  ticket_price: 2.99, total_tickets: 30000, min_threshold: 1, duration_days: 5,
  max_tickets_per_user: 100, subscriber_eligible: true, show_ticket_count: false,
  instant_wins_enabled: false, instant_wins: [], instant_pot: 0,
  youtube_url: "", facebook_url: "",
  skill_question: { question: "How many £10,000 bundles make £50,000?", options: ["5", "4", "3", "6"], answer_index: 0 },
};

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;
const Input = (p) => <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} />;

const fmtDate = (iso) => {
  if (!iso) return "No date set";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AdminLaunch() {
  const [draws, setDraws] = useState([]);
  const [mode, setMode] = useState("list"); // "list" | "edit"
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const fileRef = useRef(null);
  const [siteLaunch, setSiteLaunch] = useState({ date: "", time: "21:00", competition_id: "" });
  const [savingSite, setSavingSite] = useState(false);

  const loadSettings = () =>
    api.get("/admin/settings")
      .then(({ data }) => {
        const s = (data && (data.settings || data)) || {};
        if (s.launch_at) {
          const d = new Date(s.launch_at);
          setSiteLaunch((x) => ({ ...x, date: d.toISOString().slice(0, 10), time: d.toTimeString().slice(0, 5) }));
        }
        if (s.launch_competition_id) setSiteLaunch((x) => ({ ...x, competition_id: s.launch_competition_id }));
      })
      .catch(() => {});

  const saveSiteLaunch = async () => {
    setSavingSite(true);
    let iso = null;
    if (siteLaunch.date) { try { iso = new Date(`${siteLaunch.date}T${siteLaunch.time || "21:00"}:00`).toISOString(); } catch (e) { /* keep null */ } }
    try {
      await api.put("/admin/settings", { launch_at: iso, launch_competition_id: siteLaunch.competition_id || null });
      toast.success("Site go-live date saved — homepage countdown updated.");
    } catch (e) { toast.error(getErr(e)); } finally { setSavingSite(false); }
  };

  const loadDraws = () =>
    api.get("/competitions")
      .then(({ data }) => setDraws(findLaunchComps(data)))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { loadDraws(); loadSettings(); }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setNum = (k) => (e) => setF((s) => ({ ...s, [k]: Number(e.target.value) }));

  const openNew = () => { setF(EMPTY); setEditId(null); setMode("edit"); window.scrollTo(0, 0); };

  const openEdit = (comp) => {
    const at = getLaunchAt(comp);
    const dt = at ? new Date(at) : null;
    const cat = (comp.metadata_tags || []).find((t) => CATEGORIES.map((c) => c.toLowerCase()).includes((t || "").toLowerCase()));
    setF({
      ...EMPTY,
      title: comp.title, prize_title: comp.prize_title, description: comp.description, hero_image: comp.hero_image || "",
      ticket_price: comp.ticket_price, total_tickets: comp.total_tickets, min_threshold: comp.min_threshold,
      max_tickets_per_user: comp.max_tickets_per_user || 0, subscriber_eligible: !!comp.subscriber_eligible,
      show_ticket_count: !!comp.show_ticket_count,
      instant_wins_enabled: !!comp.instant_wins_enabled, instant_wins: comp.instant_wins || [], instant_pot: 0,
      youtube_url: comp.live_draw_url || "", category: cat ? cat[0].toUpperCase() + cat.slice(1) : "Cash",
      skill_question: comp.skill_question || EMPTY.skill_question,
      launch_date: dt ? dt.toISOString().slice(0, 10) : EMPTY.launch_date,
      launch_time: dt ? dt.toTimeString().slice(0, 5) : EMPTY.launch_time,
    });
    setEditId(comp.id);
    setMode("edit");
    window.scrollTo(0, 0);
  };

  const removeFromLaunch = async (comp) => {
    if (!window.confirm(`Remove "${comp.title}" from launch draws? It will stay as a normal competition (not deleted).`)) return;
    const tags = (comp.metadata_tags || []).filter((t) => t !== LAUNCH_TAG && !(typeof t === "string" && t.startsWith(LAUNCH_AT_PREFIX)));
    try { await api.put(`/admin/competitions/${comp.id}`, { metadata_tags: tags, featured: false }); toast.success("Removed from launch draws."); loadDraws(); }
    catch (e) { toast.error(getErr(e)); }
  };

  const aiDescribe = async () => {
    const prompt = aiPrompt || f.title;
    if (!prompt) { toast.error("Type what you're giving away first."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai/describe", { prompt });
      setF((s) => ({ ...s, title: data.title || s.title, description: data.copy || s.description }));
      toast.success("AI copy generated.");
    } catch (e) { toast.error(getErr(e)); } finally { setAiBusy(false); }
  };
  const parsePrize = (txt) => { const m = String(txt || "").replace(/[, ]/g, "").match(/\d+(?:\.\d+)?/); return m ? Number(m[0]) : 50000; };
  const aiPricing = (mode2) => { setF((s) => ({ ...s, ...recommendPricing(parsePrize(s.prize_title), s.instant_pot, mode2) })); toast.success("AI pricing applied."); };
  const aiFairness = () => { setF((s) => ({ ...s, max_tickets_per_user: recommendFairnessCap(f.total_tickets) })); toast.success("AI set a fairness cap."); };
  const aiDivide = () => { const wins = divideInstantWins(f.instant_pot, f.total_tickets); if (!wins.length) { toast.error("Enter a total instant-win value first."); return; } setF((s) => ({ ...s, instant_wins_enabled: true, instant_wins: wins })); toast.success(`Divided ${gbp(f.instant_pot)} into ${wins.length} instant prizes.`); };
  const aiSkill = () => { const g = randomSkill(); const correct = g.options[g.answer_index]; const rest = g.options.filter((_, i) => i !== g.answer_index); setF((s) => ({ ...s, skill_question: { question: g.question, options: [correct, ...rest], answer_index: 0 } })); toast.success("Skill question generated."); };

  const addInstant = () => setF((s) => ({ ...s, instant_wins: [...(s.instant_wins || []), { ticket_number: Math.floor(Math.random() * (s.total_tickets || 1000)) + 1, prize_type: "cash", label: "Cash Instant Win", value: 50 }] }));
  const delInstant = (i) => setF((s) => ({ ...s, instant_wins: s.instant_wins.filter((_, idx) => idx !== i) }));

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)."); return; }
    const r = new FileReader(); r.onload = () => setF((s) => ({ ...s, hero_image: r.result })); r.readAsDataURL(file);
  };
  const aiHeroImage = async () => {
    try { await api.post("/admin/ai/hero-image", { prompt: f.title }); toast.success("Ad image generated."); }
    catch (e) { toast.message("AI image generation isn't enabled on your backend yet — upload a photo or paste a hero image URL instead."); }
  };

  const launchISO = () => {
    try { return new Date(`${f.launch_date}T${f.launch_time || "21:00"}:00`).toISOString(); }
    catch { return new Date(Date.now() + 7 * 86400000).toISOString(); }
  };

  const save = async () => {
    if (!f.title || !f.prize_title) { toast.error("Title and prize title are required."); return; }
    if (!f.skill_question.question) { toast.error("Add a skill question."); return; }
    setSaving(true);
    const tags = [f.category, LAUNCH_TAG, `${LAUNCH_AT_PREFIX}${launchISO()}`];
    if (f.facebook_url) tags.push(`fb:${f.facebook_url}`);
    const payload = {
      title: f.title, description: f.description, prize_title: f.prize_title,
      prize_value: parsePrize(f.prize_title), cash_alternative: parsePrize(f.prize_title), hero_image: f.hero_image,
      ticket_price: Number(f.ticket_price), total_tickets: Number(f.total_tickets), min_threshold: Number(f.min_threshold),
      ends_at: launchISO(), auto_draw_at: launchISO(), featured: true,
      // Real spec fields so the backend + both clients treat this as a launch special.
      launch_draw: true, launch_day: true,
      show_ticket_count: !!f.show_ticket_count,
      instant_wins_value: Number(f.instant_pot) || (f.instant_wins || []).reduce((s, w) => s + (Number(w.value) || 0), 0),
      skill_question: f.skill_question,
      instant_wins_enabled: f.instant_wins_enabled, instant_wins: (f.instant_wins || []).map((w) => ({ ticket_number: Number(w.ticket_number), prize_type: w.prize_type, label: w.label, value: Number(w.value) })),
      live_draw_enabled: !!f.youtube_url, live_draw_url: f.youtube_url, free_postal_enabled: true,
      metadata_tags: tags, subscriber_eligible: f.subscriber_eligible, max_tickets_per_user: Number(f.max_tickets_per_user),
    };
    try {
      if (editId) { await api.put(`/admin/competitions/${editId}`, payload); toast.success("Launch draw updated — homepage synced."); }
      else { await api.post("/admin/competitions", payload); toast.success("Launch draw created — homepage synced."); }
      await loadDraws();
      setMode("list");
      window.scrollTo(0, 0);
    } catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  // ---------------- LIST VIEW ----------------
  if (mode === "list") {
    return (
      <div data-testid="admin-launch" className="max-w-3xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Rocket className="h-6 w-6 text-[var(--neon-gold)]" /><h2 className="font-display uppercase text-2xl text-white">Launch Day Special Draws</h2></div>
          <NeonButton variant="pink" onClick={openNew} data-testid="launch-add"><Plus className="h-4 w-4" /> Add Draw</NeonButton>
        </div>
        <p className="mt-2 text-sm text-[var(--text-muted)]">
          Create as many launch-night specials as you like. They appear on the homepage straight away so people can enter before go-live, and each is drawn on the date/time you set below.
        </p>

        {/* Site go-live setter -> writes config.launch_at + launch_competition_id */}
        <div className="countdown-gold mt-5"><div className="neon-inner p-5">
          <div className="flex items-center gap-2 text-[var(--neon-gold)]"><Calendar className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">Site go-live date</span></div>
          <p className="mt-1 text-sm text-[var(--text-muted)]">Drives the homepage launch countdown + fireworks. Until this date, launch specials are the only purchasable competitions; everything else is browse-only.</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div><Label>Go-live date</Label><Input type="date" value={siteLaunch.date} onChange={(e) => setSiteLaunch((s) => ({ ...s, date: e.target.value }))} data-testid="site-launch-date" /></div>
            <div><Label>Time (HH:MM)</Label><Input type="time" value={siteLaunch.time} onChange={(e) => setSiteLaunch((s) => ({ ...s, time: e.target.value }))} data-testid="site-launch-time" /></div>
            <div>
              <Label>Headline launch competition</Label>
              <select value={siteLaunch.competition_id} onChange={(e) => setSiteLaunch((s) => ({ ...s, competition_id: e.target.value }))} data-testid="site-launch-comp"
                className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]">
                <option value="">— none —</option>
                {draws.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <NeonButton variant="pink" onClick={saveSiteLaunch} loading={savingSite} data-testid="site-launch-save"><Save className="h-4 w-4" /> Save Go-Live Date</NeonButton>
            {siteLaunch.date && <span className="text-xs text-[var(--neon-gold)]">Launches {fmtDate(`${siteLaunch.date}T${siteLaunch.time || "21:00"}:00`)}</span>}
            <button onClick={() => setSiteLaunch({ date: "", time: "21:00", competition_id: "" })} className="text-xs text-[var(--text-muted)] hover:text-white" data-testid="site-launch-clear">Clear date</button>
          </div>
        </div></div>

        {draws.length === 0 ? (
          <NeonCard variant="soft" className="mt-6" innerClassName="p-8 text-center">
            <Rocket className="mx-auto h-8 w-8 text-[var(--neon-gold)]" />
            <p className="mt-3 font-display uppercase text-white">No launch draws yet</p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Add your first launch-night special to start the hype.</p>
            <NeonButton variant="pink" className="mt-4" onClick={openNew}><Plus className="h-4 w-4" /> Add Launch Draw</NeonButton>
          </NeonCard>
        ) : (
          <div className="mt-6 space-y-3" data-testid="launch-draw-list">
            {draws.map((c) => (
              <NeonCard key={c.id} variant="flow" innerClassName="p-4">
                <div className="flex items-center gap-4">
                  {c.hero_image
                    ? <img src={c.hero_image} alt={c.title} className="h-16 w-16 rounded-lg object-cover border border-white/10" />
                    : <div className="h-16 w-16 rounded-lg border border-dashed border-white/20 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-white/30" /></div>}
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-white truncate">{c.title}</p>
                    <p className="text-xs text-[var(--text-muted)] truncate">{c.prize_title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                      <Pill color="cyan">{gbp(c.ticket_price)}/ticket</Pill>
                      <span className="inline-flex items-center gap-1 text-[var(--neon-gold)]"><Calendar className="h-3 w-3" /> {fmtDate(getLaunchAt(c))}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(c)} className="text-[var(--neon-cyan)] hover:brightness-110" data-testid={`launch-edit-${c.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => removeFromLaunch(c)} className="text-[var(--neon-pink)] hover:brightness-110" data-testid={`launch-remove-${c.id}`} aria-label="Remove from launch draws" title="Remove from launch draws"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </NeonCard>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------------- EDIT / CREATE VIEW ----------------
  return (
    <div data-testid="admin-launch-edit" className="max-w-3xl">
      <div className="flex items-center justify-between">
        <button onClick={() => { setMode("list"); window.scrollTo(0, 0); }} className="flex items-center gap-1 text-sm text-[var(--neon-cyan)]" data-testid="launch-back"><ArrowLeft className="h-4 w-4" /> Back to draws</button>
        {editId ? <Pill color="green">Editing</Pill> : <Pill color="gold">New draw</Pill>}
      </div>
      <div className="mt-3 flex items-center gap-2"><Rocket className="h-6 w-6 text-[var(--neon-gold)]" /><h2 className="font-display uppercase text-2xl text-white">{editId ? "Edit Launch Draw" : "New Launch Draw"}</h2></div>

      {/* Draw date / drawn-on */}
      <div className="countdown-gold mt-4"><div className="neon-inner p-5">
        <div className="flex items-center gap-2 text-[var(--neon-gold)]"><Rocket className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">Drawn on launch night</span></div>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Set when this draw closes &amp; is drawn (launch evening). The homepage countdown follows the earliest launch draw.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div><Label>Draw date</Label><Input type="date" value={f.launch_date} onChange={set("launch_date")} data-testid="launch-date" /></div>
          <div><Label>Time (HH:MM)</Label><Input type="time" value={f.launch_time} onChange={set("launch_time")} data-testid="launch-time" /></div>
        </div>
      </div></div>

      {/* AI Describer */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-5">
        <p className="flex items-center gap-1 text-xs font-display uppercase tracking-widest glow-cyan"><Sparkles className="h-3.5 w-3.5" /> AI Describer</p>
        <Input className="mt-2 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. brand new tesla model 3, neon vibe" data-testid="launch-ai-prompt" />
        <NeonButton variant="outlinePink" className="mt-3 w-full" onClick={aiDescribe} loading={aiBusy} data-testid="launch-ai-generate"><Wand2 className="h-4 w-4" /> Generate Copy</NeonButton>
      </NeonCard>

      <div className="mt-4 space-y-3">
        <div><Label>Title</Label><Input value={f.title} onChange={set("title")} data-testid="launch-title" /></div>
        <div><Label>Prize Title</Label><Input value={f.prize_title} onChange={set("prize_title")} /></div>
        <div><Label>Description</Label><textarea value={f.description} onChange={set("description")} rows={3} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" /></div>
        <div><Label>Hero Image URL</Label><Input value={f.hero_image} onChange={set("hero_image")} placeholder="https://... or upload below" /></div>
        <div>
          <Label>Category</Label>
          <Input value={f.category} onChange={set("category")} />
          <div className="mt-2 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setF((s) => ({ ...s, category: c }))} className={`rounded-full px-4 py-1.5 text-sm ${f.category === c ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)]"}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Prize photo + AI hero image */}
      <NeonCard variant="soft" className="mt-4" innerClassName="p-5">
        <Label>Prize photo</Label>
        {f.hero_image ? <img src={f.hero_image} alt="prize" className="mt-2 max-h-48 rounded-lg border border-white/10" /> : <div className="mt-2 h-32 rounded-lg border border-dashed border-white/20" />}
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        <div className="mt-3 flex gap-2">
          <NeonButton variant="outlinePink" className="flex-1" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4" /> Upload</NeonButton>
          <NeonButton variant="outlineCyan" className="flex-1" onClick={aiHeroImage}><ImageIcon className="h-4 w-4" /> AI Ad Image</NeonButton>
        </div>
      </NeonCard>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div><Label>YouTube live-draw link</Label><Input value={f.youtube_url} onChange={set("youtube_url")} placeholder="https://youtube.com/..." /></div>
        <div><Label>Facebook live-draw link</Label><Input value={f.facebook_url} onChange={set("facebook_url")} placeholder="https://facebook.com/..." /></div>
      </div>

      {/* AI ticket pricing */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-5">
        <p className="flex items-center gap-1 text-xs font-display uppercase tracking-widest glow-cyan"><Sparkles className="h-3.5 w-3.5" /> AI Ticket Pricing</p>
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">Suggests ticket price, quantity &amp; break-even from your prize value + instant wins (keeps ~30% profit).</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <NeonButton variant="outlinePink" onClick={() => aiPricing("cheap")} data-testid="launch-pricing-cheap"><Bot className="h-4 w-4" /> Cheap &amp; High Volume</NeonButton>
          <NeonButton variant="outlinePink" onClick={() => aiPricing("balanced")} data-testid="launch-pricing-balanced"><Scale className="h-4 w-4" /> Average / Balanced</NeonButton>
        </div>
      </NeonCard>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div><Label>Ticket Price £</Label><Input type="number" step="0.01" value={f.ticket_price} onChange={setNum("ticket_price")} /></div>
        <div><Label>Total Tickets</Label><Input type="number" value={f.total_tickets} onChange={setNum("total_tickets")} /></div>
        <div><Label>Min Threshold</Label><Input type="number" value={f.min_threshold} onChange={setNum("min_threshold")} /></div>
        <div><Label>Duration (days)</Label><Input type="number" value={f.duration_days} onChange={setNum("duration_days")} /></div>
      </div>

      <div className="mt-4 rounded-xl border border-[rgba(255,45,149,.4)] p-3">
        <div className="flex items-center justify-between"><span className="text-xs font-display uppercase tracking-widest glow-pink">Fairness Cap</span>
          <button onClick={aiFairness} className="text-xs text-[var(--neon-cyan)] flex items-center gap-1" data-testid="launch-fairness"><Bot className="h-3.5 w-3.5" /> AI Recommend</button></div>
        <div className="mt-2"><Label>Max tickets per person (0 = global limit)</Label><Input type="number" value={f.max_tickets_per_user} onChange={setNum("max_tickets_per_user")} /></div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-white"><input type="checkbox" checked={f.subscriber_eligible} onChange={(e) => setF((s) => ({ ...s, subscriber_eligible: e.target.checked }))} className="accent-[var(--neon-cyan)] h-5 w-5" /> Members get free tickets into this competition</label>

      <label className="mt-3 flex items-center gap-2 text-white"><input type="checkbox" checked={f.show_ticket_count} onChange={(e) => setF((s) => ({ ...s, show_ticket_count: e.target.checked }))} data-testid="launch-show-tickets" className="accent-[var(--neon-cyan)] h-5 w-5" /> Show ticket counts publicly (reveal "N of T left" on this competition)</label>

      {/* Instant wins */}
      <div className="mt-4 rounded-xl border border-[rgba(0,255,163,.5)] p-3">
        <label className="flex items-center justify-between cursor-pointer"><span className="flex items-center gap-2 font-display uppercase tracking-widest glow-green">⚡ Instant Wins</span>
          <input type="checkbox" checked={f.instant_wins_enabled} onChange={(e) => setF((s) => ({ ...s, instant_wins_enabled: e.target.checked }))} data-testid="launch-instant-toggle" className="accent-[var(--neon-green)] h-5 w-5" /></label>
        {f.instant_wins_enabled && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 items-end">
              <div><Label>Total instant-win value £</Label><Input type="number" value={f.instant_pot} onChange={setNum("instant_pot")} /></div>
              <NeonButton variant="outlinePink" onClick={aiDivide} data-testid="launch-divide"><Bot className="h-4 w-4" /> AI Recommend &amp; Divide</NeonButton>
            </div>
            {(f.instant_wins || []).map((w, i) => (
              <div key={i} className="grid grid-cols-[70px_1fr_80px_30px] gap-2 items-center text-xs">
                <span className="text-[var(--text-muted)]">#{w.ticket_number}</span>
                <span className="text-white truncate">{w.label}</span>
                <span className="text-[var(--neon-green)]">{gbp(w.value)}</span>
                <button onClick={() => delInstant(i)} className="text-[var(--neon-pink)]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <NeonButton variant="outlineCyan" className="w-full" onClick={addInstant}><Plus className="h-4 w-4" /> Add Instant Prize</NeonButton>
          </div>
        )}
      </div>

      {/* Skill question */}
      <div className="mt-4 rounded-xl border border-white/10 p-3">
        <div className="flex items-center justify-between"><span className="text-xs font-display uppercase tracking-widest text-white">Skill Question</span>
          <button onClick={aiSkill} className="text-xs text-[var(--neon-gold)] flex items-center gap-1" data-testid="launch-skill-generate"><Sparkles className="h-3.5 w-3.5" /> Auto-generate</button></div>
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">A genuine skill question is a UK legal requirement for paid prize competitions.</p>
        <div className="mt-2"><Label>Question</Label><Input value={f.skill_question.question} onChange={(e) => setF((s) => ({ ...s, skill_question: { ...s.skill_question, question: e.target.value } }))} data-testid="launch-question" /></div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="mt-2"><Label>Option {i + 1}{i === 0 ? " (correct)" : ""}</Label>
            <Input value={f.skill_question.options[i] || ""} onChange={(e) => setF((s) => { const opts = [...s.skill_question.options]; opts[i] = e.target.value; return { ...s, skill_question: { ...s.skill_question, options: opts, answer_index: 0 } }; })} data-testid={`launch-option-${i}`} /></div>
        ))}
        <p className="mt-2 text-[11px] text-[var(--text-muted)]">Option 1 is the correct answer.</p>
      </div>

      <div className="mt-5 flex gap-2">
        <NeonButton variant="pink" className="flex-1" onClick={save} loading={saving} data-testid="launch-save"><Save className="h-4 w-4" /> {editId ? "Save Launch Draw" : "Create Launch Draw"}</NeonButton>
        <NeonButton variant="outlineCyan" onClick={() => { setMode("list"); window.scrollTo(0, 0); }}>Cancel</NeonButton>
      </div>
      <p className="mt-2 text-center text-[11px] text-[var(--text-muted)]">Saved to your shared backend and shown on the homepage launch section automatically.</p>
    </div>
  );
}
